import { Alert, Empty, Spin, Typography } from 'antd';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useAuthenticatedRequest, UserContext } from '../UserContext';
import { formatDateWithZone } from '../pages/agentLeads/format-date';

const API_BASE = '/api/lead-engagement/leads';

const STYLES = `
/* The app root (.App) resets text-align to center; every plain block of text in here needs
   to opt back out explicitly or it silently inherits that centering. */
.lead-facts { color: #262626; font-size: 14px; line-height: 1.5; text-align: left; }
.lead-facts .lf-summary { line-height: 1.6; }
.lead-facts .lf-section + .lf-section { margin-top: 20px; }
.lead-facts .lf-heading {
  border-bottom: 1px solid #f0f0f0;
  color: var(--hw-accent, #5b1eaf);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
  padding-bottom: 6px;
  text-transform: uppercase;
}
.lead-facts .lf-rows-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}
.lead-facts .lf-row {
  column-gap: 16px;
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  margin: 0 -8px;
  padding: 8px;
}
.lead-facts .lf-row-wide { grid-column: 1 / -1; }
.lead-facts .lf-row > * { min-width: 0; }
.lead-facts .lf-row:nth-child(even) { background: #fafafa; }
.lead-facts .lf-rows-grid .lf-row { background: none; }
.lead-facts .lf-label { color: #8c8c8c; font-weight: 500; }
.lead-facts .lf-meta { color: #8c8c8c; }
.lead-facts .lf-num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.lead-facts .lf-evidence {
  color: #8c8c8c;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 4px;
}
.lead-facts .lf-location + .lf-location { margin-top: 10px; }
.lead-facts .lf-nest {
  column-gap: 12px;
  display: grid;
  grid-template-columns: 130px minmax(0, 1fr);
  row-gap: 4px;
}
.lead-facts .lf-nest > * { min-width: 0; }
.lead-facts .lf-stack > * + * { margin-top: 4px; }
.lead-facts .lf-scroll { max-width: 100%; overflow-x: auto; }
.lead-facts .lf-table { border-collapse: collapse; font-size: 13px; }
.lead-facts .lf-table th,
.lead-facts .lf-table td {
  border-bottom: 1px solid #f0f0f0;
  padding: 6px 16px 6px 0;
  text-align: left;
  vertical-align: top;
}
.lead-facts .lf-table th { color: #8c8c8c; font-weight: 500; white-space: nowrap; }
.lead-facts .lf-table tr:last-child td { border-bottom: 0; }
@media (max-width: 520px) {
  .lead-facts .lf-row { grid-template-columns: minmax(0, 1fr); }
}
`;

interface SoftBlock {
  kind: 'has_agent' | 'recently_transacted' | 'not_in_market' | 'paused';
  observed_at: string | null;
}

interface SearchProfile {
  areas?: string[];
  min_price: number | null;
  max_price: number | null;
  min_beds: number | null;
  max_beds: number | null;
  home_types?: string[];
  price_evidence: string;
}

interface SearchLocation {
  text: string;
  purpose: string;
  zip_code: string | null;
  evidence: string;
  observed_at: string | null;
}

interface LeadFacts {
  role?:
    | 'buyer'
    | 'seller'
    | 'buyer+seller'
    | 'investor'
    | 'renter'
    | 'unknown';
  role_evidence: string;
  person_summary: string;
  search_profile: SearchProfile | null;
  search_locations?: SearchLocation[];
  bad_phone: boolean;
  bad_phone_evidence: string;
  soft_blocks?: SoftBlock[];
  location: string;
  location_evidence: string;
  timezone: string;
  preferred_language: string;
  preferred_language_evidence: string;
}

interface LeadFactsResponse {
  lead_id: string;
  signals?: unknown;
  data?: unknown;
}

interface LeadFactsProps {
  apiUrl: string;
  leadId: string;
  timezone: string;
}

const prettifyBlockKind = (kind: string): string => {
  const map: Record<string, string> = {
    has_agent: 'Has agent',
    recently_transacted: 'Recently transacted',
    not_in_market: 'Not in market',
    paused: 'Paused',
  };
  return map[kind] || kind;
};

const prettifyRole = (role: string): string =>
  role
    .split('+')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' + ');

const formatPrice = (value: number): string =>
  `$${Math.round(value).toLocaleString()}`;

const joinList = (values: string[]): string => values.join(' · ');

const EM_DASH = '—';

const LEAD_FACTS_KEY = 'lead_facts';

const MAX_DEPTH = 6;

const ZONED = /(?:[Zz]|[+-]\d{2}:?\d{2})$/;

const NAIVE_MIDNIGHT = /^(\d{4}-\d{2}-\d{2})T00:00:00(?:\.0+)?$/;

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isScalar = (value: unknown): boolean =>
  value === null || typeof value !== 'object';

const isEmptyValue = (value: unknown): boolean =>
  (Array.isArray(value) && value.length === 0) ||
  (isPlainObject(value) && Object.keys(value).length === 0);

const scalarText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return EM_DASH;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};

const prettifyKey = (key: string): string => {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : EM_DASH;
};

const formatTimestamp = (value: string, timezone: string): string => {
  if (ZONED.test(value)) {
    return formatDateWithZone(value, timezone);
  }
  return NAIVE_MIDNIGHT.exec(value)?.[1] ?? value;
};

const ScalarValue = ({ value }: { value: unknown }) =>
  typeof value === 'number' ? (
    <span className="lf-num">{String(value)}</span>
  ) : (
    <>{scalarText(value)}</>
  );

const NestedObject = ({
  value,
  depth,
}: {
  value: Record<string, unknown>;
  depth: number;
}) => {
  const entries = Object.entries(value).filter(
    ([, entry]) => !isEmptyValue(entry),
  );
  if (entries.length === 0) {
    return null;
  }
  return (
    <div className="lf-nest">
      {entries.map(([key, entry]) => (
        <Fragment key={key}>
          <div className="lf-label">{prettifyKey(key)}</div>
          <div>{renderValue(entry, depth + 1)}</div>
        </Fragment>
      ))}
    </div>
  );
};

const ObjectTable = ({
  rows,
  depth,
}: {
  rows: Record<string, unknown>[];
  depth: number;
}) => {
  const columns: string[] = [];
  rows.forEach(row =>
    Object.keys(row).forEach(key => {
      if (!columns.includes(key)) {
        columns.push(key);
      }
    }),
  );
  if (columns.length === 0) {
    return null;
  }
  return (
    <div className="lf-scroll">
      <table className="lf-table">
        <thead>
          <tr>
            {columns.map(column => (
              <th key={column}>{prettifyKey(column)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map(column => (
                <td key={column}>{renderValue(row[column], depth + 1)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// The merged data blob is stored and served verbatim so a newer derivation's fields
// survive a deployment that predates them. Rendering is therefore driven by the shape of
// the JSON rather than by a known key list — a key nobody here has heard of still shows up.
function renderValue(value: unknown, depth: number): React.ReactNode {
  if (depth >= MAX_DEPTH) {
    return <span className="lf-meta">{JSON.stringify(value)}</span>;
  }
  if (Array.isArray(value)) {
    if (value.every(isScalar)) {
      return joinList(value.map(scalarText));
    }
    if (value.every(isPlainObject)) {
      return <ObjectTable depth={depth} rows={value} />;
    }
    return (
      <div className="lf-stack">
        {value.map((item, index) => (
          <div key={index}>{renderValue(item, depth + 1)}</div>
        ))}
      </div>
    );
  }
  if (isPlainObject(value)) {
    return <NestedObject depth={depth} value={value} />;
  }
  return <ScalarValue value={value} />;
}

interface SignalRow {
  key: string;
  value: unknown;
  source: string | null;
  confidence: number | null;
  observedAt: string | null;
  processedAt: string | null;
}

const asText = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const parseSignals = (raw: unknown): SignalRow[] =>
  (Array.isArray(raw) ? raw : []).filter(isPlainObject).map(signal => ({
    key: asText(signal.key) ?? '',
    value: signal.value,
    source: asText(signal.source),
    confidence: asNumber(signal.confidence),
    observedAt: asText(signal.observed_at),
    processedAt: asText(signal.processed_at),
  }));

// `lead_facts` is one key of the merged data blob like any other; the profile above reads it
// from there, and parseDataEntries drops it so it is not also rendered raw under DATA.
const parseFacts = (raw: unknown): LeadFacts | null => {
  if (!isPlainObject(raw)) {
    return null;
  }
  const facts = raw[LEAD_FACTS_KEY];
  return isPlainObject(facts) ? (facts as unknown as LeadFacts) : null;
};

const parseDataEntries = (raw: unknown): [string, unknown][] =>
  isPlainObject(raw)
    ? Object.entries(raw).filter(
        ([key, value]) => key !== LEAD_FACTS_KEY && !isEmptyValue(value),
      )
    : [];

interface FactRow {
  key: string;
  label: string;
  value: React.ReactNode;
  evidence?: string;
  /** Spans the full grid width instead of flowing two-up — for prose fields like Summary. */
  wide?: boolean;
}

const FactRowView = ({ row }: { row: FactRow }) => (
  <div className={row.wide ? 'lf-row lf-row-wide' : 'lf-row'}>
    <div className="lf-label">{row.label}</div>
    <div>
      <div>{row.value}</div>
      {row.evidence && <div className="lf-evidence">{row.evidence}</div>}
    </div>
  </div>
);

const SignalsTable = ({
  signals,
  timezone,
}: {
  signals: SignalRow[];
  timezone: string;
}) => {
  const showValue = signals.some(
    signal =>
      signal.value !== 'true' &&
      signal.value !== null &&
      signal.value !== undefined &&
      signal.value !== '',
  );
  const showObserved = signals.some(signal => signal.observedAt);
  const showConfidence = signals.some(
    signal => signal.confidence !== null && signal.confidence !== 1,
  );
  const showProcessed = signals.some(signal => signal.processedAt);

  return (
    <div className="lf-scroll">
      <table className="lf-table">
        <thead>
          <tr>
            <th>Signal</th>
            {showValue && <th>Value</th>}
            {showObserved && <th>Observed</th>}
            <th>Source</th>
            {showConfidence && <th>Confidence</th>}
            {showProcessed && <th>Processed</th>}
          </tr>
        </thead>
        <tbody>
          {signals.map((signal, index) => (
            <tr key={`${signal.source ?? ''}-${signal.key}-${index}`}>
              <td>{prettifyKey(signal.key)}</td>
              {showValue && (
                <td>
                  {signal.value === 'true' ? '' : scalarText(signal.value)}
                </td>
              )}
              {showObserved && (
                <td className="lf-num">
                  {signal.observedAt
                    ? formatTimestamp(signal.observedAt, timezone)
                    : EM_DASH}
                </td>
              )}
              <td>{signal.source ? prettifyKey(signal.source) : EM_DASH}</td>
              {showConfidence && (
                <td className="lf-num">
                  {signal.confidence === null
                    ? EM_DASH
                    : String(signal.confidence)}
                </td>
              )}
              {showProcessed && (
                <td className="lf-num lf-meta">
                  {signal.processedAt
                    ? formatTimestamp(signal.processedAt, timezone)
                    : EM_DASH}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const LeadFacts = ({ apiUrl, leadId, timezone }: LeadFactsProps) => {
  const user = useContext(UserContext);
  const authenticatedRequest = useAuthenticatedRequest(user?.accessToken);
  const [data, setData] = useState<LeadFactsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setData(null);
      try {
        const res = await authenticatedRequest(headers =>
          fetch(`${apiUrl}${API_BASE}/${leadId}/lead-facts`, { headers }),
        );
        // The route 404s for an unknown lead and, until it ships, for every lead — both
        // read as "nothing derived yet" rather than as an error.
        if (res.status === 404) {
          if (!cancelled) {
            setData({ lead_id: leadId });
          }
          return;
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = (await res.json()) as LeadFactsResponse;
        if (!cancelled) {
          setData(json);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, leadId, authenticatedRequest]);

  if (loading) {
    return (
      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'center',
          minHeight: '200px',
        }}
      >
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        description={error}
        message="Error Loading Lead Facts"
        showIcon
        type="error"
      />
    );
  }

  const signals = parseSignals(data?.signals);
  const dataEntries = parseDataEntries(data?.data);
  const facts = parseFacts(data?.data);

  if (!data || (!facts && signals.length === 0 && dataEntries.length === 0)) {
    return (
      <Empty
        description="No Facts Derived Yet"
        style={{ color: '#8c8c8c', margin: '60px 0' }}
      >
        <Typography.Text type="secondary">
          Lead facts are derived as engagement data is collected.
        </Typography.Text>
      </Empty>
    );
  }

  const searchProfile = facts?.search_profile ?? null;
  const priceRange = searchProfile
    ? [searchProfile.min_price, searchProfile.max_price]
        .filter((value): value is number => value !== null)
        .map(formatPrice)
        .join(' - ')
    : '';
  const bedsRange = searchProfile
    ? [searchProfile.min_beds, searchProfile.max_beds]
        .filter((value): value is number => value !== null)
        .join(' - ')
    : '';

  // The API hands back the stored derivation verbatim, so a dump written before a field
  // existed simply has no key for it — every read below stays guarded or defaulted.
  const searchLocations = facts?.search_locations ?? [];
  const softBlocks = facts?.soft_blocks ?? [];
  const role = facts?.role ?? 'unknown';

  const rows: FactRow[] = [];

  if (facts) {
    if (facts.person_summary) {
      rows.push({
        key: 'summary',
        label: 'Summary',
        value: <div className="lf-summary">{facts.person_summary}</div>,
        wide: true,
      });
    }

    rows.push({
      key: 'role',
      label: 'Role',
      value: prettifyRole(role),
      evidence: facts.role_evidence,
    });

    if (priceRange) {
      rows.push({
        key: 'price',
        label: 'Price range',
        value: <span className="lf-num">{priceRange}</span>,
        evidence: searchProfile?.price_evidence,
      });
    }

    if (bedsRange) {
      rows.push({
        key: 'beds',
        label: 'Bedrooms',
        value: <span className="lf-num">{bedsRange}</span>,
      });
    }

    if (searchProfile?.home_types?.length) {
      rows.push({
        key: 'home-types',
        label: 'Home types',
        value: joinList(searchProfile.home_types),
      });
    }

    if (searchProfile?.areas?.length) {
      rows.push({
        key: 'areas',
        label: 'Areas',
        value: joinList(searchProfile.areas),
      });
    }

    if (facts.location) {
      rows.push({
        key: 'location',
        label: 'Location',
        value: facts.location,
        evidence: facts.location_evidence,
      });
    }

    if (facts.timezone) {
      rows.push({ key: 'timezone', label: 'Timezone', value: facts.timezone });
    }

    if (facts.preferred_language) {
      rows.push({
        key: 'preferred-language',
        label: 'Preferred language',
        value: facts.preferred_language,
        evidence: facts.preferred_language_evidence,
      });
    }

    if (facts.bad_phone) {
      rows.push({
        key: 'bad-phone',
        label: 'Bad phone',
        value: 'Yes',
        evidence: facts.bad_phone_evidence,
      });
    }

    if (softBlocks.length) {
      rows.push({
        key: 'soft-blocks',
        label: 'Soft blocks',
        value: joinList(
          softBlocks.map(
            block =>
              `${prettifyBlockKind(block.kind)}${
                block.observed_at ? ` (${block.observed_at})` : ''
              }`,
          ),
        ),
      });
    }
  }

  return (
    <div className="lead-facts">
      <style>{STYLES}</style>

      {rows.length > 0 && (
        <div className="lf-section">
          <div className="lf-heading">Profile</div>
          <div className="lf-rows-grid">
            {rows.map(row => (
              <FactRowView key={row.key} row={row} />
            ))}
          </div>
        </div>
      )}

      {searchLocations.length > 0 && (
        <div className="lf-section">
          <div className="lf-heading">Search locations</div>
          {searchLocations.map(location => (
            <div
              className="lf-location"
              key={`${location.text}-${location.observed_at ?? ''}`}
            >
              <div>
                {location.text}
                {location.purpose && (
                  <span className="lf-meta"> · {location.purpose}</span>
                )}
                {location.observed_at && (
                  <span className="lf-meta"> · {location.observed_at}</span>
                )}
              </div>
              {location.evidence && (
                <div className="lf-evidence">{location.evidence}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {signals.length > 0 && (
        <div className="lf-section">
          <div className="lf-heading">Signals</div>
          <SignalsTable signals={signals} timezone={timezone} />
        </div>
      )}

      {dataEntries.length > 0 && (
        <div className="lf-section">
          <div className="lf-heading">Data</div>
          <div>
            {dataEntries.map(([key, value]) => (
              <FactRowView
                key={key}
                row={{
                  key,
                  label: prettifyKey(key),
                  value: renderValue(value, 0),
                }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
